import { Injectable } from '@angular/core';

import { WindowService } from './window.service';

export enum PlatformType {
    PC,
    Mac,
    WindowsPhone,
    iPad,
    iPhone,
    AndroidPhone,
    AndroidTablet,
    Linux,
    Chromebook,
    Other
}

export enum BrowserType {
    IE,
    Edge,
    EdgeLegacy,
    Chrome,
    Firefox,
    Safari,
    PhantomJS,
    Opera,
    WebView,
    Other
}

export enum NetworkType {
    Slow2g = 'Slow2g',
    Standard2g = 'Standard2g',
    Standard3g = 'Standard3g',
    Standard4g = 'Standard4g',
    Unknown = 'Unknown'
}

export interface PlatformInfo {
    /**
     * @arg keyword: The word being looked for in a user agent string to determine the platform type.
     */
    keyword: string;
    platformType: PlatformType;

    /**
     * The products.office.com platform specific page name
     */
    pageName?: string;
}

export class PlatformVersion {

    public major: number;
    public minor: number;
    public build: number;

    constructor(majorPart: number | string, minorPart: number | string, buildPart: number | string) {
        this.major = this.normalize(majorPart);
        this.minor = this.normalize(minorPart);
        this.build = this.normalize(buildPart);
    }

    public compareTo(version: PlatformVersion): number {
        if (this.major === version.major && this.minor === version.minor && this.build === version.build) {
            return 0;
        }

        if (this.major > version.major) {
            return 1;
        }

        if (this.major === version.major && this.minor > version.minor) {
            return 1;
        }

        if (this.major === version.major && this.minor === version.minor && this.build > version.build) {
            return 1;
        }

        return -1;
    }

    public toString(): string {
        return `${this.major}_${this.minor}_${this.build}`;
    }

    private normalize(part: number | string): number {
        if (typeof part === 'number') {
            return part;
        } else if (typeof part === 'string') {
            const result = parseInt(part);
            return isNaN(result) ? 0 : result;
        }

        return 0;
    }
}

export interface BrowserInfo {
    /**
     * @arg keyword: The word being looked for in a user agent string to determine the browser type.
     */
    keyword: string;
    browserType: BrowserType;
}

export interface DeviceInfo {
    platform: PlatformInfo;
    platformVersion: PlatformVersion;
    browser: BrowserInfo;
}

export interface NetworkInfo {
    keyword: string;
    networkType: NetworkType;
}

@Injectable({
    providedIn: 'root'
})
export class DeviceDetector {

    public userAgent: string;
    public deviceInfo: DeviceInfo;
    public networkType: NetworkType;

    constructor(
        private readonly windowService: WindowService
    ) {
        this.userAgent = this.windowService.nativeWindow.navigator.userAgent;
    }

    private readonly platforms: Array<PlatformInfo> = [
        { keyword: 'Windows NT', platformType: PlatformType.PC },
        { keyword: 'Windows XP', platformType: PlatformType.PC },
        { keyword: 'Macintosh', platformType: PlatformType.Mac },
        { keyword: 'Windows Phone', platformType: PlatformType.WindowsPhone, pageName: 'windows-phone' },
        { keyword: 'iPad', platformType: PlatformType.iPad, pageName: 'ipad' },
        { keyword: 'iPhone', platformType: PlatformType.iPhone, pageName: 'iphone' },
        { keyword: 'Android', platformType: PlatformType.AndroidPhone, pageName: 'android-phone' },
        { keyword: 'Linux', platformType: PlatformType.Linux },  // This should be below Android since "Linux" shows up in the Android UserAgent string as well
        { keyword: 'CrOS', platformType: PlatformType.Chromebook }
    ];

    private readonly browsers: Array<BrowserInfo> = [
        { keyword: 'PhantomJS', browserType: BrowserType.PhantomJS },
        { keyword: 'Edge', browserType: BrowserType.EdgeLegacy },
        { keyword: 'EdgA', browserType: BrowserType.Edge },
        { keyword: 'EdgiOS', browserType: BrowserType.Edge },
        { keyword: 'Edg', browserType: BrowserType.Edge },
        { keyword: 'wv', browserType: BrowserType.WebView },
        { keyword: 'MSIE', browserType: BrowserType.IE },
        { keyword: 'Trident', browserType: BrowserType.IE },
        { keyword: 'OPR', browserType: BrowserType.Opera },
        { keyword: 'Chrome', browserType: BrowserType.Chrome },
        { keyword: 'CriOS', browserType: BrowserType.Chrome },
        { keyword: 'Firefox', browserType: BrowserType.Firefox },
        { keyword: 'Safari', browserType: BrowserType.Safari }
    ];

    private readonly networkTypes: Array<NetworkInfo> = [
        { keyword: 'slow-2g', networkType: NetworkType.Slow2g },
        { keyword: '2g', networkType: NetworkType.Standard2g },
        { keyword: '3g', networkType: NetworkType.Standard3g },
        { keyword: '4g', networkType: NetworkType.Standard4g }
    ];

    public getDeviceInfo(): DeviceInfo {
        if (this.deviceInfo) {
            return this.deviceInfo;
        }

        const loweredUserAgentString = this.userAgent.toLowerCase();

        let platform: PlatformInfo = { keyword: 'Other', platformType: PlatformType.Other };
        this.platforms.some(value => {
            if (loweredUserAgentString.indexOf(value.keyword.toLowerCase()) !== -1) {
                platform = value;
                return true;
            }

            return false;
        });

        // Can't use keyword alone to distinguish between Android Phone and Android tablet
        if (platform.platformType === PlatformType.AndroidPhone && loweredUserAgentString.indexOf('mobile') === -1) {
            platform = { keyword: 'Android', platformType: PlatformType.AndroidTablet, pageName: 'android-tablet' };
        }

        let version = new PlatformVersion(0, 0, 0);
        if (platform.platformType === PlatformType.Mac) {
            // This is to extract the Mac OS version, e.g.
            // Mozilla/5.0 (Macintosh; Intel Mac OS X 10_10_1) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/41.0.2227.1 Safari/537.36
            // Mozilla/5.0 (Macintosh; Intel Mac OS X 10.6; rv:25.0) Gecko/20100101 Firefox/25.0
            const match = /Mac OS X (\d+)(_|\.)(\d+)([_\.]?)(\d*)/i.exec(this.userAgent);
            if (match.length >= 6) {
                version = new PlatformVersion(match[1], match[3], match[5]);
            }
        } else if (platform.platformType === PlatformType.PC || platform.platformType === PlatformType.WindowsPhone) {
            if (loweredUserAgentString.indexOf('windows xp') !== -1) {
                // XP shows up as either just xp or nt 5.1
                version = new PlatformVersion(5, 1, 0);
            } else {
                // This is to extract the Windows version, e.g.
                // Mozilla/5.0 (Windows NT 10.0; <64-bit tags>) AppleWebKit/<WebKit Rev> (KHTML, like Gecko) Chrome/<Chrome Rev> Safari/<WebKit Rev> Edge/<EdgeHTML Rev>.<Windows Build>
                // Mozilla/5.0 (Windows Phone 10.0; Android <Android Version>; <Device Manufacturer>; <Device Model>) AppleWebKit/<WebKit Rev> (KHTML, like Gecko) Chrome/<Chrome Rev> Mobile Safari/<WebKit Rev> Edge/<EdgeHTML Rev>.<Windows Build>
                const match = /Windows (NT|Phone) (OS )?(\d+)\.(\d+)/i.exec(this.userAgent);
                if (match.length >= 5) {
                    version = new PlatformVersion(match[3], match[4], 0);
                }
            }
        } else if (platform.platformType === PlatformType.iPad || platform.platformType === PlatformType.iPhone) {
            const match = loweredUserAgentString.match(/os (\d+)_(\d+)(_(\d+))?/);
            const major = match[1] || 0;
            const minor = match[2] || 0;
            const buildPart = match[4] || 0;
            version = new PlatformVersion(major, minor, buildPart);
        }

        let browser: BrowserInfo = { keyword: 'Other', browserType: BrowserType.Other };
        this.browsers.some(value => {
            if (loweredUserAgentString.indexOf(value.keyword.toLowerCase()) !== -1) {
                browser = value;
                return true;
            }

            return false;
        });

        this.deviceInfo = {
            platform,
            platformVersion: version,
            browser
        };

        return this.deviceInfo;
    }

    public getNetworkInfo(): NetworkType {
        if (this.networkType) {
            return this.networkType;
        }

        const connection = this.windowService.nativeWindow.navigator['connection'];
        const effectiveType = connection && connection.effectiveType ? connection.effectiveType : 'Unknown';
        let networkInfo: NetworkInfo = { keyword: 'Unknown', networkType: NetworkType.Unknown };
        this.networkTypes.some(value => {
            if (effectiveType.toLowerCase().indexOf(value.keyword) !== -1) {
                networkInfo = value;
                return true;
            }

            return false;
        });

        this.networkType = networkInfo.networkType;
        return this.networkType;
    }

    public isDesktopDevice(): boolean {
        const platformType = this.getDeviceInfo().platform.platformType;
        return platformType === PlatformType.PC || platformType === PlatformType.Mac || platformType === PlatformType.Linux || platformType === PlatformType.Chromebook;
    }

    public isMobileDevice(): boolean {
        const platformType = this.getDeviceInfo().platform.platformType;
        return platformType === PlatformType.iPhone ||
            platformType === PlatformType.AndroidPhone ||
            platformType === PlatformType.WindowsPhone;
    }

    public isTabletDevice(filteredType: PlatformType = null): boolean {
        const platformType = this.getDeviceInfo().platform.platformType;
        if (filteredType !== null) {
            return platformType === filteredType;
        }

        return platformType === PlatformType.iPad ||
            platformType === PlatformType.AndroidTablet;
    }

    public isUwp(): boolean {
        return this.userAgent.toLowerCase().indexOf('msapphost/') > -1 && typeof(Windows) !== 'undefined';
    }

    public isWebView2(): boolean {
        return this.isUwp() && typeof(window.chrome) !== 'undefined' && typeof(window.chrome.webview) !== 'undefined';
    }

    public isIE11Browser(): boolean {
        return this.userAgent.toLowerCase().indexOf('trident/7.0') > -1;
    }
}
